import React, { useEffect, useState } from 'react';
import { useApiClient } from '../api/client';
import { InterestTranslationDraft, InterestTranslationFields } from '../components/InterestTranslationFields';
import { PageHeader, PageShell } from '../components/ui';
import { useNavigate } from 'react-router-dom';

const emptyTranslations: InterestTranslationDraft = { translationKey: '', nameEn: '', nameDe: '', nameCs: '' };

export const CreateInterestTypePage: React.FC = () => {
  const { request } = useApiClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [translations, setTranslations] = useState(emptyTranslations);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await request('/interests/types', {
        method: 'POST',
        body: JSON.stringify({ name, ...translations }),
      });
      navigate('/interests');
    } catch (e) {
      setError((e as Error)?.message || 'Could not create interest type.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <PageShell>
      <PageHeader title="New interest type" description="Create a category to organize related profile interests." actions={<button type="button" className="btn btn-neutral" onClick={() => navigate('/interests')}>Cancel</button>} />
      <form onSubmit={submit} className="admin-panel max-w-3xl p-5 sm:p-6">
        {error && <div className="admin-interest-alert mb-5" role="alert">{error}</div>}
        <div className="grid gap-5">
          <label className="admin-form-label">Type name (internal key)<input className="form-control mt-1 w-full" placeholder="Type name (internal key)" value={name} onChange={(event) => setName(event.target.value)} required /></label>
          <InterestTranslationFields value={translations} onChange={(field, value) => setTranslations((previous) => ({ ...previous, [field]: value }))} />
          <div className="flex justify-end gap-2 border-t pt-4">
            <button type="button" className="btn btn-neutral" onClick={() => navigate('/interests')}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={!name.trim() || loading}>{loading ? 'Saving…' : 'Create type'}</button>
          </div>
        </div>
      </form>
    </PageShell>
  );
};

type InterestTypeOption = { id: string; name: string };

export const CreateInterestPage: React.FC = () => {
  const { request } = useApiClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [typeId, setTypeId] = useState('');
  const [translations, setTranslations] = useState(emptyTranslations);
  const [types, setTypes] = useState<InterestTypeOption[]>([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [typesError, setTypesError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    request<InterestTypeOption[]>('/interests/types')
      .then((result) => { if (active) setTypes(result); })
      .catch(() => { if (active) setTypesError(true); })
      .finally(() => { if (active) setTypesLoading(false); });
    return () => { active = false; };
  }, [request]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await request('/interests', {
        method: 'POST',
        body: JSON.stringify({ name, typeId, ...translations }),
      });
      navigate('/interests');
    } catch (e) {
      setError((e as Error)?.message || 'Could not create interest.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <PageShell>
      <PageHeader title="New interest" description="Add an option people can select in their profiles." actions={<button type="button" className="btn btn-neutral" onClick={() => navigate('/interests')}>Cancel</button>} />
      <form onSubmit={submit} className="admin-panel max-w-3xl p-5 sm:p-6">
        {error && <div className="admin-interest-alert mb-5" role="alert">{error}</div>}
        <div className="grid gap-5">
          <label className="admin-form-label">Interest name (internal key)<input className="form-control mt-1 w-full" placeholder="Interest name (internal key)" value={name} onChange={(event) => setName(event.target.value)} required /></label>
          <div>
            <label className="admin-form-label" htmlFor="interest-type">Type</label>
            {typesError ? (
              <p className="admin-interest-alert mt-2" role="alert">Could not load interest types. Return to the interests page and try again.</p>
            ) : (
              <select id="interest-type" className="form-control mt-1 w-full" value={typeId} onChange={(event) => setTypeId(event.target.value)} required disabled={typesLoading || !types.length}>
                <option value="">{typesLoading ? 'Loading types…' : 'Choose type…'}</option>
                {types.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
              </select>
            )}
            {!typesLoading && !typesError && !types.length && <p className="mt-2 text-xs text-amber-800">Create an interest type before adding interests.</p>}
          </div>
          <InterestTranslationFields value={translations} onChange={(field, value) => setTranslations((previous) => ({ ...previous, [field]: value }))} />
          <div className="flex justify-end gap-2 border-t pt-4">
            <button type="button" className="btn btn-neutral" onClick={() => navigate('/interests')}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={!name.trim() || !typeId || loading || typesLoading || typesError}>{loading ? 'Saving…' : 'Create interest'}</button>
          </div>
        </div>
      </form>
    </PageShell>
  );
};
